import { useEffect, useState } from "react";
import { Award, Upload, Clock, CheckCircle2, ImagePlus } from "lucide-react";
import Btn from "../components/Btn";
import api, { API } from "../lib/api";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Menunggu Review", cls: "text-su-energy bg-su-energy/15", icon: Clock },
  approved: { label: "Tampil di Beranda", cls: "text-su-success bg-su-success/15", icon: CheckCircle2 },
};

export default function MyTestimonial() {
  const [stories, setStories] = useState([]);
  const [comment, setComment] = useState("");
  const [position, setPosition] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/success-stories/mine").then(({ data }) => setStories(data.stories)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (f.size > 5 * 1024 * 1024) return toast.error("Ukuran maksimal 5MB");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return toast.error("Tambahkan foto kamu");
    if (comment.trim().length < 10) return toast.error("Testimoni minimal 10 karakter");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("comment", comment.trim());
      fd.append("position", position.trim());
      await api.post("/success-stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Testimoni terkirim! Menunggu persetujuan admin.");
      setComment(""); setPosition(""); setFile(null); setPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim testimoni");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="my-testimonial">
      <div>
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-su-primary/15 mb-3"><Award className="w-6 h-6 text-su-primary" /></div>
        <h1 className="font-heading font-black text-3xl">Testimoni Saya</h1>
        <p className="text-su-muted font-medium mt-2">Sudah lolos lewat SkorUp.io? Bagikan kisah suksesmu! Setelah disetujui admin, testimonimu tampil di Beranda dan menginspirasi pejuang lain.</p>
      </div>

      <div className="card-chunky p-6 space-y-4" data-testid="story-form">
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-su-border grid place-items-center overflow-hidden bg-su-bg shrink-0">
            {preview ? <img src={preview} alt="preview" className="w-full h-full object-cover" /> : <ImagePlus className="w-8 h-8 text-su-muted" />}
          </div>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">
              <Upload className="w-4 h-4" /> {file ? "Ganti Foto" : "Unggah Foto"}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="story-file" />
            <p className="text-xs text-su-muted font-medium mt-2">JPG/PNG, maks 5MB.</p>
          </label>
        </div>
        <div>
          <label className="text-xs font-heading font-black uppercase text-su-muted">Posisi / Instansi Diterima</label>
          <input value={position} onChange={(e) => setPosition(e.target.value)} maxLength={120} data-testid="story-position"
            placeholder="cth: Analis Kebijakan — Kemenkeu RI / Mahasiswa Kedokteran UI"
            className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary" />
        </div>
        <div>
          <label className="text-xs font-heading font-black uppercase text-su-muted">Testimoni Kamu</label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={600} rows={4} data-testid="story-comment"
            placeholder="Ceritakan pengalaman & bagaimana SkorUp.io membantumu lolos..."
            className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium outline-none focus:border-su-primary resize-none" />
          <p className="text-xs text-su-muted text-right">{comment.length}/600</p>
        </div>
        <Btn onClick={submit} disabled={busy} className="w-full" data-testid="story-submit">{busy ? "Mengirim..." : "Kirim Testimoni"}</Btn>
      </div>

      {stories.length > 0 && (
        <div>
          <h2 className="font-heading font-black text-lg mb-3">Riwayat Testimoni</h2>
          <div className="space-y-3">
            {stories.map((s) => {
              const st = STATUS[s.status] || STATUS.pending;
              return (
                <div key={s.id} className="card-chunky p-4 flex items-start gap-3" data-testid={`my-story-${s.id}`}>
                  <img src={`${API}/success-stories/${s.id}/photo`} alt="" className="w-12 h-12 rounded-xl object-cover border border-su-border shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  <div className="flex-1 min-w-0">
                    {s.position && <p className="font-heading font-bold text-sm text-su-success">{s.position}</p>}
                    <p className="text-sm text-su-muted line-clamp-2">"{s.comment}"</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs font-heading font-black px-2 py-1 rounded-full shrink-0 ${st.cls}`}><st.icon className="w-3 h-3" />{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
